/**
 * Reader actions (the prototype's `moveMessage`, read toggle, `pickTopics`, `copyMarkdown`, Undo):
 * every message mutation updates the query cache first (useOptimistic), then calls the engine; an error
 * rolls back and is reported (plan errors open the upsell); success shows the toast with Undo.
 */

import { useQueryClient } from '@tanstack/react-query';
import type { FolderId, MessageDetail, MessageId, MessageRow, SidebarCounts } from '@digester/core';
import { reportError } from '../app/errors.js';
import { openDialog } from '../app/dialogs.js';
import { showToast, undoAll } from '../app/Toast.js';
import { ui } from '../app/ui-store.js';
import { useEngine, useOptimistic } from '../engine-client/EngineProvider.js';
import { qk } from '../engine-client/keys.js';
import type { Patcher } from '../engine-client/optimistic.js';
import type { Lookups } from './data.js';

type CountsPatch = (c: SidebarCounts) => SidebarCounts;

function bump(c: SidebarCounts, folderId: string, total: number, unread: number): SidebarCounts {
  const prev = c.folders[folderId];
  if (!prev) return c;
  return { ...c, folders: { ...c.folders, [folderId]: { total: Math.max(0, prev.total + total), unread: Math.max(0, prev.unread + unread) } } };
}

type Page = { rows: MessageRow[]; unread: number; unreadMinutes: number };
const isPage = (v: unknown): v is Page => typeof v === 'object' && v !== null && Array.isArray((v as Page).rows);
const isPages = (v: unknown): v is { pages: unknown[] } => typeof v === 'object' && v !== null && Array.isArray((v as { pages: unknown[] }).pages);

/** Shift a cached list's unread totals when `row` (if it is in that list, unread state != read) changes. */
function adjustUnread(data: unknown, row: MessageRow, read: boolean): unknown {
  const pages: unknown[] = isPages(data) ? data.pages : [data];
  const holds = pages.some((p) => isPage(p) && p.rows.some((r) => r.id === row.id && r.read !== read));
  if (!holds) return data;
  const sign = read ? -1 : 1;
  const fix = (p: unknown): unknown =>
    isPage(p) ? { ...p, unread: Math.max(0, p.unread + sign), unreadMinutes: Math.max(0, p.unreadMinutes + sign * row.readingMinutes) } : p;
  return isPages(data) ? { ...data, pages: data.pages.map(fix) } : fix(data);
}

const listScope =(key: readonly unknown[]): { kind?: string; folderId?: string } | undefined => (key[1] as { scope?: { kind?: string; folderId?: string } } | undefined)?.scope;

export interface ReaderActions {
  move(row: MessageRow, folderId: FolderId): Promise<void>;
  setRead(row: MessageRow, read: boolean, options?: { quiet?: boolean }): Promise<void>;
  copyMarkdown(row: MessageRow): Promise<void>;
  pickTopics(row: MessageRow): void;
  /** Z: undo the last undoable action (the one the toast offers). */
  undoLast(): Promise<void>;
}

export function useReaderActions(lookups: Lookups): ReaderActions {
  const engine = useEngine();
  const optimistic = useOptimistic();
  const qc = useQueryClient();

  const patchCounts = (p: Patcher, fn: CountsPatch): void => p.patch<SidebarCounts>(qk.counts(), fn);
  const patchDetail = (p: Patcher, id: MessageId, fn: (d: MessageDetail) => MessageDetail): void => p.patch<MessageDetail>(qk.message(id), fn);

  return {
    async move(row, folderId) {
      if (row.folderId === folderId) return;
      const target = lookups.folder(folderId);
      const from = row.folderId;
      try {
        const result = await optimistic(
          (p) => {
            // Lists of other folders lose the row; every other list (views, all, search) re-labels it.
            p.removeRows(['list'], [row.id], { where: (key) => listScope(key)?.kind === 'folder' && listScope(key)?.folderId !== folderId });
            p.patchRows(['list'], [row.id], (r) => ({ ...r, folderId }));
            patchCounts(p, (c) => bump(bump(c, from, -1, row.read ? 0 : -1), folderId, 1, row.read ? 0 : 1));
            patchDetail(p, row.id, (d) => ({
              ...d,
              message: { ...d.message, placement: { ...d.message.placement, folderId, placedBy: { kind: 'user', id: null }, folderEnteredAt: Date.now() as MessageDetail['message']['placement']['folderEnteredAt'] } },
            }));
          },
          () => engine.messages.move([row.id], folderId),
        );
        showToast(`Moved to ${target?.name ?? 'folder'}`, result.undoId);
      } catch (error) {
        reportError(error);
      }
    },

    async setRead(row, read, options = {}) {
      if (row.read === read) return;
      try {
        const result = await optimistic(
          (p) => {
            // Unread totals (footer: "N unread · M min to read") of every cached list holding the row.
            p.patch<unknown>(['list'], (old) => adjustUnread(old, row, read));
            p.patchRows(['list'], [row.id], (r) => ({ ...r, read }));
            patchCounts(p, (c) => bump(c, row.folderId, 0, read ? -1 : 1));
            patchDetail(p, row.id, (d) => ({ ...d, message: { ...d.message, readAt: read ? (Date.now() as MessageDetail['message']['readAt']) : null } }));
          },
          () => engine.messages.setRead([row.id], read),
        );
        if (!options.quiet) showToast(read ? 'Marked read' : 'Marked unread', result.undoId);
      } catch (error) {
        reportError(error);
      }
    },

    async copyMarkdown(row) {
      let text: string;
      try {
        text = await engine.messages.markdown(row.id);
      } catch (error) {
        reportError(error);
        return;
      }
      try {
        await navigator.clipboard.writeText(text);
        showToast('Copied as Markdown');
      } catch {
        openDialog('copy-markdown', { text });
      }
    },

    pickTopics(row) {
      openDialog('topics', { messageId: row.id });
    },

    async undoLast() {
      const undoIds = ui.get().toast?.undoIds ?? [];
      if (undoIds.length === 0) {
        showToast('Nothing to undo');
        return;
      }
      try {
        await undoAll(engine, undoIds);
        showToast('Undone');
        void qc.invalidateQueries({ queryKey: qk.lists() });
      } catch (error) {
        reportError(error);
      }
    },
  };
}
